/** Ranged skill for enemies — fires a projectile via spawn callback at the windup→recovery transition. */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyRangedConfig {
  windupMs: number;
  recoveryMs: number;
  cooldownMs: number;
  range: number;
  projectileSpeed: number;
  projectileDamage: number;
  projectileLifetimeMs: number;
  projectileSize: number;
  projectileColor: number;
  attackType: string;
}

/** Callback signature for spawning a projectile into the scene. */
export type ProjectileSpawner = (
  x: number, y: number, targetX: number, targetY: number,
  config: { speed: number; damage: number; lifetimeMs: number; size: number; color: number; attackType: string },
) => void;

/**
 * Ranged skill: windup (aim telegraph) → recovery (projectile fires on entry) → cooldown.
 * No active phase — the projectile handles its own physics overlap for damage.
 */
export class EnemyRangedSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyRangedConfig;
  private _spawner: ProjectileSpawner | null = null;

  /** Target captured at activation for aim direction. */
  private _targetX: number = 0;
  private _targetY: number = 0;
  private _originX: number = 0;
  private _originY: number = 0;

  constructor(skillId: string, config: EnemyRangedConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'recovery') {
      this.fireProjectile();
    }
  }

  get skillRange(): number { return this.config.range; }

  /** Set the projectile spawn callback (wired by EnemyManager after construction). */
  setSpawner(spawner: ProjectileSpawner): void {
    this._spawner = spawner;
  }

  /** Set the target position before calling activate(). */
  setTarget(enemyX: number, enemyY: number, playerX: number, playerY: number): void {
    this._originX = enemyX;
    this._originY = enemyY;
    this._targetX = playerX;
    this._targetY = playerY;
  }

  /** Ranged skills don't use proximity damage — projectile handles its own physics overlap. */
  isInDamagePhase(): boolean { return false; }

  /** No movement during ranged attack — stand still to fire. */
  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'windup' || this.phase === 'recovery') {
      return { vx: 0, vy: 0 };
    }
    return null;
  }

  /** No proximity damage — projectile uses physics overlap. */
  getDamageInfo(): EnemySkillDamageInfo | null { return null; }

  destroy(): void {
    super.destroy();
    this._spawner = null;
  }

  private fireProjectile(): void {
    if (!this._spawner) return;
    this._spawner(
      this._originX, this._originY,
      this._targetX, this._targetY,
      {
        speed: this.config.projectileSpeed,
        damage: this.config.projectileDamage,
        lifetimeMs: this.config.projectileLifetimeMs,
        size: this.config.projectileSize,
        color: this.config.projectileColor,
        attackType: this.config.attackType,
      },
    );
  }
}
